import { Theme, useTheme } from "@mui/material/styles";
import { Box, ResponsiveStyleValue } from "@mui/system";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { Song } from "~/types";
import PlayingBars from "./PlayingBars";
import { useAudioCurrentState } from "./useAudio";


// Helper to convert MUI responsive sizes into an HTML sizes string
const getResponsiveSizes = (
  size: ResponsiveStyleValue<number | string> | undefined,
  theme: Theme,
) => {
  if (!size) return "245px";
  if (typeof size === "number") return `${size}px`;
  if (typeof size === "string") return size;

  if (typeof size === "object") {
    // Check breakpoints from largest to smallest
    const order: (keyof Theme["breakpoints"]["values"])[] = [
      "xl",
      "lg",
      "md",
      "sm",
      "xs",
    ];
    const queries: string[] = [];

    order.forEach((key) => {
      const sObj = size as Record<string, number | string | undefined>;
      if (sObj[key] !== undefined) {
        const val = sObj[key];
        const width = typeof val === "number" ? `${val}px` : val;

        if (key === "xs") {
          queries.push(width as string);
        } else {
          queries.push(
            `(min-width: ${theme.breakpoints.values[key]}px) ${width}`,
          );
        }
      }
    });

    return queries.join(", ") || "245px";
  }

  return "245px";
};

type SongArtworkProps = {
  song: Song;
  size?: ResponsiveStyleValue<number | string>;
  hidePlayingBars?: boolean;
  disableVideo?: boolean;
};

const SongArtwork = ({
  song,
  size,
  hidePlayingBars,
  disableVideo,
}: SongArtworkProps) => {
  const { isCurrent, isCurrentPlaying } = useAudioCurrentState(song.id);
  const theme = useTheme();

  let unoptimized = true; // always unoptimized to prevent next/image caching issues with frequently updated artwork
  if (song.artworkSrc?.indexOf("/anonymous/") !== -1) {
    // For anonymous URLs, we can allow optimization, no auth
    unoptimized = false;
  }

  let videoSrc = song.videoSrc;
  if (!song.videoSrc && !song.artworkSrc) {
    // Fallback to default animation if no video or artwork
    videoSrc = "/logo_animation.mp4";
  }
  if (disableVideo) {
    videoSrc = undefined;
  }

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoSrc) return;

    let cancelled = false;

    const inferMimeFromExtension = (src: string | undefined) => {
      if (!src) return undefined;
      const s = src.split("?")[0].toLowerCase();
      if (s.endsWith(".mp4")) return "video/mp4";
      if (s.endsWith(".m4v")) return "video/mp4";
      if (s.endsWith(".webm")) return "video/webm";
      if (s.endsWith(".mov")) return "video/quicktime";
      if (s.endsWith(".mkv")) return "video/x-matroska";
      return undefined;
    };

    (async () => {
      try {
        const res = await fetch(videoSrc, { cache: "no-store" });
        if (cancelled) return;

        // Prefer server-provided content-type when valid
        const contentType = res.headers.get("content-type") || undefined;

        // Read full body as ArrayBuffer to ensure complete data before creating Blob
        const buffer = await res.arrayBuffer();
        if (cancelled) return;

        let mime = undefined;
        if (contentType && contentType !== "application/octet-stream") {
          mime = contentType.split(";")[0].trim();
        }
        if (!mime) {
          mime = inferMimeFromExtension(videoSrc) || "video/mp4";
        }

        const blob = new Blob([buffer], { type: mime });
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        blobUrlRef.current = url;
        v.src = url;
        try {
          v.defaultMuted = true;
          v.muted = true;
        } catch (err) {
          // defensive: ignore if browser blocks setting
        }
      } catch (err) {
        // ignore fetch/parse errors
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [videoSrc]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !blobUrlRef.current) return;
    if (isCurrentPlaying) {
      v.play().catch(() => undefined);
    } else {
      v.pause();
    }
  }, [isCurrentPlaying]);

  const resolveFirstSize = (s: SongArtworkProps["size"]) => {
    if (typeof s === "number") return s;
    if (typeof s === "string") return undefined;
    return undefined;
  };

  const firstSize = resolveFirstSize(size);
  const playingBarSize =
    typeof firstSize === "number"
      ? firstSize <= 200
        ? "small"
        : "medium"
      : "medium";

  // Calculate the sizes string for the browser
  const responsiveSizes = getResponsiveSizes(size, theme);

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        aspectRatio: "1 / 1",
        zIndex: 0,
      }}
    >
      {videoSrc && (
        <video
          ref={videoRef}
          key={videoSrc}
          preload="none"
          loop
          autoPlay={isCurrentPlaying}
          muted
          playsInline
          disablePictureInPicture
          onLoadedMetadata={(e) => {
            try {
              (e.currentTarget as HTMLVideoElement).muted = true;
            } catch (err) {
              // defensive
            }
          }}
          onError={(e) => {
            const ve = (e.currentTarget as HTMLVideoElement).error;
            console.error("[SongArtwork] video:error", {
              src: videoSrc,
              songId: song.id,
              errorCode: ve?.code,
              errorMessage: ve?.message,
            });
          }}
          style={{
            objectFit: "cover",
            opacity: isCurrentPlaying ? 1 : 0,
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            transition: "opacity 0.5s ease",
            zIndex: 1,
          }}
        />
      )}
      {song.artworkSrc ? (
        <Image
          src={song.artworkSrc}
          alt={song.title}
          fill
          unoptimized={unoptimized}
          sizes={responsiveSizes}
          style={{ objectFit: "cover" }}
          priority={isCurrent}
        />
      ) : (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Image
            src="/logo.png"
            alt={song.title}
            fill
            sizes={responsiveSizes}
            style={{ objectFit: "cover" }}
          />
        </Box>
      )}

      {/* Playing bars overlay */}
      {isCurrent && !hidePlayingBars && (
        <Box
          className="playingBars"
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            bgcolor: "action.disabledBackground",
            borderRadius: 1,
            px: 0.5,
            py: 0.25,
            display: "flex",
            alignItems: "flex-end",
            zIndex: 2,
          }}
        >
          <PlayingBars isPlaying={isCurrentPlaying} size={playingBarSize} />
        </Box>
      )}
    </Box>
  );
};

export default SongArtwork;
